import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography
} from "antd";
import { FileOutlined, MinusCircleOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import {
  createContract,
  getContract,
  openDocument,
  reextractContract,
  updateContract
} from "./api";
import {
  BILLING_INTERVAL_LABELS,
  CATEGORY_LABELS,
  NOTICE_TO_LABELS,
  NOTICE_UNIT_LABELS,
  PRICE_BASIS_LABELS,
  RENEWAL_LABELS,
  STATUS_LABELS
} from "./constants";
import TermsSummary from "./TermsSummary";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./vendorContracts.css";

const DATE_FIELDS = ["startDate", "minimumTermEndDate", "cancelledOn", "endsOn"];

const toFormValues = contract => {
  const values = { ...contract };
  DATE_FIELDS.forEach(field => {
    values[field] = contract[field] ? dayjs(contract[field]) : null;
  });
  if (values.noticePeriodUnit === "unknown") values.noticePeriodUnit = undefined;
  return values;
};

const fromFormValues = values => {
  const body = { ...values };
  DATE_FIELDS.forEach(field => {
    body[field] = values[field] ? values[field].format("YYYY-MM-DD") : "";
  });
  if (!body.noticePeriodUnit) body.noticePeriodUnit = "unknown";
  return body;
};

const NEW_CONTRACT = {
  category: "other",
  currency: "EUR",
  billingInterval: "monthly",
  priceBasis: "gross",
  renewalType: "unknown",
  noticeTo: "unknown",
  status: "active",
  reminderDays: 30,
  keyFacts: []
};

const options = (labels, t) =>
  Object.entries(labels).map(([value, label]) => ({ value, label: t(label) }));

/** Edits a contract; for one the assistant just read, this is where the result is checked. */
const VendorContractDrawer = ({ contractId, open, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [form] = Form.useForm();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMobile = useIsMobile();
  const status = Form.useWatch("status", form);
  const renewalType = Form.useWatch("renewalType", form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (!contractId) {
      setContract(null);
      form.setFieldsValue(NEW_CONTRACT);
      return;
    }
    setLoading(true);
    getContract(contractId)
      .then(res => {
        setContract(res.data.data);
        form.setFieldsValue(toFormValues(res.data.data));
      })
      .catch(err => {
        generalErrorAlert(err);
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, contractId]);

  const uncertain = new Set(contract?.reviewed ? [] : contract?.extraction?.uncertainFields || []);
  // Props for a Form.Item the assistant was unsure about.
  const check = name =>
    uncertain.has(name) ? { validateStatus: "warning", help: t("Please check this value") } : {};

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const body = fromFormValues(values);
      const res = contractId ? await updateContract(contractId, body) : await createContract(body);
      Success(t("Contract saved"));
      onSaved(res.data.data);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  const handleReextract = async () => {
    try {
      await reextractContract(contractId, (lng || "de").slice(0, 2));
      Success(t("The contract is being read again."));
      onSaved(null);
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const extraction = contract?.extraction;

  // Phones: pinned in the footer, where the thumb is. Desktop keeps them in the header.
  const actions = (
    <Space className="vc-drawer-actions">
      <Button onClick={onClose}>{t("Cancel")}</Button>
      <Button type="primary" loading={saving} disabled={loading} onClick={handleSave}>
        {contract && !contract.reviewed ? t("Confirm and save") : t("Save")}
      </Button>
    </Space>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 760}
      rootClassName="vc-drawer"
      destroyOnClose
      title={contractId ? contract?.title || t("Contract") : t("Add contract")}
      extra={isMobile ? null : actions}
      footer={isMobile ? actions : null}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 12 }} />
      ) : (
        <>
          {contract && <TermsSummary contract={contract} lng={lng} />}

          {extraction?.status === "failed" && (
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message={t("The contract could not be read")}
              description={extraction.error}
              action={
                <Button size="small" icon={<ReloadOutlined />} onClick={handleReextract}>
                  {t("Try again")}
                </Button>
              }
            />
          )}
          {extraction?.status === "done" && extraction.isContract === false && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={t("This document does not look like a contract.")}
            />
          )}
          {extraction?.status === "done" && !contract.reviewed && (
            <Alert
              type="info"
              showIcon
              className="mb-3"
              message={t("Read by the assistant – please check the highlighted fields and save.")}
              description={
                <>
                  {extraction.summary && <p className="mb-1">{extraction.summary}</p>}
                  {extraction.warnings?.length > 0 && (
                    <ul className="vc-warnings">
                      {extraction.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  )}
                </>
              }
            />
          )}

          {contract?.documents?.length > 0 && (
            <Space wrap className="mb-3">
              {contract.documents.map(document => (
                <Button
                  key={document._id}
                  size="small"
                  icon={<FileOutlined />}
                  onClick={() => openDocument(contract._id, document._id).catch(generalErrorAlert)}
                >
                  {document.name}
                </Button>
              ))}
              {extraction?.status === "done" && (
                <Button
                  size="small"
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={handleReextract}
                >
                  {t("Read again")}
                </Button>
              )}
            </Space>
          )}

          <Form form={form} layout="vertical" requiredMark={false}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={16}>
                <Form.Item
                  name="title"
                  label={t("Name")}
                  rules={[{ required: true, whitespace: true, message: t("Please enter a name") }]}
                >
                  <Input placeholder={t("e.g. Mobile phone Telekom 0170 1234567")} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="category" label={t("Category")} {...check("category")}>
                  <Select options={options(CATEGORY_LABELS, t)} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="providerName" label={t("Provider")} {...check("providerName")}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="subject"
                  label={t("Covers")}
                  tooltip={t("Phone number, licence plate, address …")}
                  {...check("subject")}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="tariffName" label={t("Tariff / product")} {...check("tariffName")}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item
                  name="contractNumber"
                  label={t("Contract number")}
                  {...check("contractNumber")}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item
                  name="customerNumber"
                  label={t("Customer number")}
                  {...check("customerNumber")}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="start">{t("Costs")}</Divider>
            <Row gutter={[16, 0]}>
              <Col xs={12} md={6}>
                <Form.Item name="price" label={t("Price")} {...check("price")}>
                  <InputNumber min={0} step={0.01} decimalSeparator="," style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="billingInterval" label={t("Billed")} {...check("billingInterval")}>
                  <Select options={options(BILLING_INTERVAL_LABELS, t)} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="priceBasis" label={t("Price is")} {...check("priceBasis")}>
                  <Select options={options(PRICE_BASIS_LABELS, t)} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="oneTimeFee" label={t("One-time fee")} {...check("oneTimeFee")}>
                  <InputNumber min={0} step={0.01} decimalSeparator="," style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="priceNotes"
                  label={t("Price notes")}
                  tooltip={t("Introductory prices, discounts, price increases")}
                  {...check("priceNotes")}
                >
                  <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
                </Form.Item>
              </Col>
              <Form.Item name="currency" hidden>
                <Input />
              </Form.Item>
            </Row>

            <Divider orientation="start">{t("Term and cancellation")}</Divider>
            <Row gutter={[16, 0]}>
              <Col xs={12} md={8}>
                <Form.Item name="startDate" label={t("Start")} {...check("startDate")}>
                  <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item
                  name="minimumTermMonths"
                  label={t("Minimum term (months)")}
                  {...check("minimumTermMonths")}
                >
                  <InputNumber min={0} max={600} precision={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="minimumTermEndDate"
                  label={t("Minimum term ends")}
                  tooltip={t("Only if the contract states the date. Otherwise it is calculated.")}
                  {...check("minimumTermEndDate")}
                >
                  <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={renewalType === "renews" ? 16 : 24}>
                <Form.Item
                  name="renewalType"
                  label={t("After the minimum term")}
                  {...check("renewalType")}
                >
                  <Select options={options(RENEWAL_LABELS, t)} />
                </Form.Item>
              </Col>
              {renewalType === "renews" && (
                <Col xs={24} md={8}>
                  <Form.Item
                    name="renewalTermMonths"
                    label={t("Renews by (months)")}
                    {...check("renewalTermMonths")}
                  >
                    <InputNumber min={1} max={120} precision={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              )}
              <Col xs={12} md={8}>
                <Form.Item
                  name="noticePeriodValue"
                  label={t("Notice period")}
                  {...check("noticePeriodValue")}
                >
                  <InputNumber min={0} max={1000} precision={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item name="noticePeriodUnit" label=" " {...check("noticePeriodUnit")}>
                  <Select allowClear options={options(NOTICE_UNIT_LABELS, t)} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="noticeTo" label={t("Notice to")} {...check("noticeTo")}>
                  <Select options={options(NOTICE_TO_LABELS, t)} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="cancellationMethod"
                  label={t("How to cancel")}
                  tooltip={t("Form and address, e.g. in writing to …, online portal")}
                  {...check("cancellationMethod")}
                >
                  <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="specialTerminationRights"
                  label={t("Special termination rights")}
                  {...check("specialTerminationRights")}
                >
                  <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item name="status" label={t("Status")}>
                  <Select options={options(STATUS_LABELS, t)} />
                </Form.Item>
              </Col>
              {status === "cancelled" && (
                <>
                  <Col xs={12} md={8}>
                    <Form.Item name="cancelledOn" label={t("Cancelled on")}>
                      <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item
                      name="endsOn"
                      label={t("Ends on")}
                      rules={[{ required: true, message: t("When does the contract end?") }]}
                    >
                      <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </>
              )}
              {status === "active" && (
                <Col xs={12} md={8}>
                  <Form.Item name="reminderDays" label={t("Warn days before the deadline")}>
                    <InputNumber min={0} max={365} precision={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Divider orientation="start">{t("Other terms")}</Divider>
            <Form.List name="keyFacts">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name }) => (
                    <div className="vc-keyfact" key={key}>
                      <Form.Item name={[name, "label"]} className="vc-keyfact-label mb-2">
                        <Input placeholder={t("e.g. Data volume")} />
                      </Form.Item>
                      <Form.Item name={[name, "value"]} className="vc-keyfact-value mb-2">
                        <Input placeholder={t("e.g. 40 GB")} />
                      </Form.Item>
                      <Button
                        type="text"
                        className="mb-2"
                        icon={<MinusCircleOutlined />}
                        aria-label={t("Remove")}
                        onClick={() => remove(name)}
                      />
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                    {t("Add term")}
                  </Button>
                </>
              )}
            </Form.List>

            <Divider orientation="start">{t("Provider contact")}</Divider>
            <Row gutter={[16, 0]}>
              <Col xs={24}>
                <Form.Item
                  name="providerAddress"
                  label={t("Address")}
                  {...check("providerAddress")}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="providerPhone" label={t("Phone")} {...check("providerPhone")}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="providerEmail" label={t("Email")} {...check("providerEmail")}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="providerWebsite"
                  label={t("Website")}
                  {...check("providerWebsite")}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="notes" label={t("Notes")}>
                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          {contract?.extraction?.model && (
            <Typography.Text type="secondary" className="vc-model-note">
              <Tag bordered={false}>
                {t("Read by {{model}}", { model: contract.extraction.model })}
              </Tag>
            </Typography.Text>
          )}
        </>
      )}
    </Drawer>
  );
};

export default VendorContractDrawer;
